import React, { useState, useEffect } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  createEventPaymentIntent,
  registerForEvent,
  sendEventConfirmationEmail,
  getStripeReceiptUrl,
} from "../api/api";
import eventsFallbackImage from "../../assets/images/events.png";
import "./eventRegistrationModal.scss";
import CloseButton from "components/CloseButton";

const getEventImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  const fileIdMatch =
    url.match(/\/file\/d\/([^/]+)/) || url.match(/[?&]id=([^&]+)/);
  if (fileIdMatch) {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    return `${backend}/api/image-proxy?id=${encodeURIComponent(fileIdMatch[1])}`;
  }
  return url;
};

const StripePaymentForm = ({
  onPaymentSuccess,
  onPaymentError,
  reservationCost,
  isSubmitting,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState("");

  useEffect(() => {
    if (stripe && elements && isSubmitting) {
      const handlePayment = async () => {
        try {
          const result = await stripe.confirmPayment({
            elements,
            redirect: "if_required",
          });
          if (result.error) {
            setPaymentError(result.error.message);
            onPaymentError(result.error.message);
          } else if (
            result.paymentIntent &&
            result.paymentIntent.status === "succeeded"
          ) {
            setPaymentError("");
            onPaymentSuccess(result.paymentIntent);
          }
        } catch (error) {
          setPaymentError("Payment failed. Please try again.");
          onPaymentError("Payment failed. Please try again.");
        }
      };
      handlePayment();
    }
  }, [stripe, elements, isSubmitting, onPaymentSuccess, onPaymentError]);

  return (
    <div
      className={`stripe-payment-container ${
        paymentError ? "payment-error" : ""
      }`}
    >
      <p>Cost Today: ${reservationCost.toFixed(2)}</p>
      <PaymentElement />
      {paymentError && (
        <div className="payment-error-message">
          <span className="error-icon">!</span>
          <span>{paymentError}</span>
        </div>
      )}
    </div>
  );
};

const formatDateWithWeekday = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return new Intl.DateTimeFormat("en-CA", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
};

const EventRegistrationModal = ({ isOpen, onClose, event, onSuccess }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dietaryRestrictions, setDietaryRestrictions] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);

  const eventName = (event?.NAME || event?.name || "").toString().trim();
  const eventTitle = eventName;
  const eventDescription = event?.DESCRIPTION || event?.description || "";
  const eventImage = getEventImageUrl(
    event?.["IMAGE URL"] ||
      event?.["Image URL"] ||
      event?.imageUrl ||
      eventsFallbackImage,
  );
  // Reservation cost might be like "$30.00"; remove non-numeric and '.' chars, then parse
  const reservationCostRaw =
    event?.["RESERVATION COST"] || event?.reservationCost || 0;
  const reservationCost =
    typeof reservationCostRaw === "string"
      ? parseFloat(reservationCostRaw.replace(/[^0-9.]/g, ""))
      : parseFloat(reservationCostRaw) || 0;
  const customFormLink =
    event?.["CUSTOM FORM LINK"] || event?.customFormLink || "";

  const eventDate = event?.DATE || event?.date;
  const eventTime = event?.TIME || event?.time;
  const eventLocation = event?.LOCATION || event?.location;
  const formattedDate = formatDateWithWeekday(eventDate) || eventDate;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/config`)
        .then((res) => res.json())
        .then((config) => {
          setStripePromise(loadStripe(config.publishableKey));
        })
        .catch(() => setError("Failed to load payment"));
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && reservationCost > 0 && stripePromise) {
      createEventPaymentIntent(eventName).then((secret) => {
        setClientSecret(secret);
      });
    }
  }, [isOpen, eventName, reservationCost, stripePromise]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required");
      return;
    }
    setIsSubmitting(true);
    setError("");
  };

  const handleStripePaymentSuccess = async (paymentIntent) => {
    try {
      await registerForEvent({
        eventName,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        dietaryRestrictions: dietaryRestrictions.trim(),
        amountPaid: reservationCost,
        paidWith: "Credit",
      });
      const receiptUrl = await getStripeReceiptUrl(paymentIntent.id);
      // await sendEventConfirmationEmail({
      //   name: name.trim(),
      //   email: email.trim(),
      //   eventTitle,
      //   customFormLink,
      //   receiptUrl,
      // });
      setShowConfirmation(true);
    } catch (err) {
      setError("Registration failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStripePaymentError = (msg) => {
    setError(msg);
    setIsSubmitting(false);
  };

  const handleNoRetainerSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError("Name and email are required");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      await registerForEvent({
        eventName,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        dietaryRestrictions: dietaryRestrictions.trim(),
        amountPaid: reservationCost,
        paidWith: "No Payment",
      });
      // await sendEventConfirmationEmail({
      //   name: name.trim(),
      //   email: email.trim(),
      //   eventTitle,
      //   customFormLink,
      //   receiptUrl: null,
      // });
      setShowConfirmation(true);
    } catch (err) {
      setError("Registration failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setName("");
      setEmail("");
      setPhone("");
      setDietaryRestrictions("");
      setError("");
      setClientSecret("");
      setShowConfirmation(false);
      onClose();
    }
  };

  const handleConfirmationDone = () => {
    onSuccess?.();
    handleClose();
  };

  if (!isOpen) return null;

  const needsPayment = reservationCost > 0 && clientSecret;

  const handleOverlayClick = () => {
    if (showConfirmation) {
      handleConfirmationDone();
    } else {
      handleClose();
    }
  };

  return (
    <div className="event-registration-overlay" onClick={handleOverlayClick}>
      <div
        className="event-registration-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="modal-close-button"
          onClick={handleOverlayClick}
          disabled={isSubmitting && !showConfirmation}
          aria-label="Close"
        >
          <CloseButton size={24} />
        </button>
        {showConfirmation ? (
          <div className="modal-confirmation">
            <div className="modal-confirmation-icon">✓</div>
            <h2 className="modal-confirmation-title">Thank you!</h2>
            <p className="modal-confirmation-message">
              Your spot has been reserved. You should receive a confirmation
              email shortly with event details and next steps.
            </p>
            <div className="modal-confirmation-details">
              <h3>{eventTitle}</h3>
              {formattedDate && (
                <p>
                  <strong>Date:</strong> {formattedDate}
                  {eventTime ? ` at ${eventTime}` : ""}
                </p>
              )}
              {eventLocation && (
                <p>
                  <strong>Location:</strong> {eventLocation}
                </p>
              )}
              {reservationCost > 0 && (
                <p>
                  <strong>Reservation cost:</strong> $
                  {reservationCost.toFixed(2)} CAD
                </p>
              )}
              {customFormLink && (
                <p className="modal-confirmation-form-link">
                  <a
                    href={customFormLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Complete additional form →
                  </a>
                </p>
              )}
            </div>
            <button
              type="button"
              className="modal-confirmation-done"
              onClick={handleConfirmationDone}
            >
              Done
            </button>
          </div>
        ) : (
          <div className="modal-layout">
            <div className="modal-panel modal-panel-image">
              <img
                src={eventImage}
                alt={eventTitle}
                className="modal-image"
                referrerPolicy="no-referrer"
              />
              <div className="modal-image-overlay">
                <h2 className="modal-title">{eventTitle}</h2>
                {eventDescription &&
                  eventDescription.split(/\n\n+/).map((para, i) => (
                    <p
                      key={i}
                      className="modal-description"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {para.trim()}
                    </p>
                  ))}
                {reservationCost > 0 && (
                  <p className="modal-reservation-cost">
                    ${reservationCost.toFixed(2)} Deposit
                  </p>
                )}
              </div>
            </div>
            <div className="modal-panel modal-panel-form">
              <div className="form-heading">
                <h3>Reserve Your Spot</h3>
                <p>{eventDate}</p>
                <p>{eventLocation}</p>
              </div>
              <form
                onSubmit={
                  reservationCost > 0 && !clientSecret
                    ? (e) => e.preventDefault()
                    : needsPayment
                      ? handleSubmit
                      : handleNoRetainerSubmit
                }
                className="registration-form"
              >
                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    disabled={isSubmitting}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="dietary">Dietary restrictions</label>
                  <input
                    id="dietary"
                    type="text"
                    value={dietaryRestrictions}
                    onChange={(e) => setDietaryRestrictions(e.target.value)}
                    placeholder="Optional"
                    disabled={isSubmitting}
                  />
                </div>
                {customFormLink && (
                  <div className="form-group">
                    <p>
                      Please fill out this form so we can make sure you're all
                      set for the event.
                    </p>
                    <a
                      className="custom-form-link"
                      href={customFormLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      → {customFormLink}
                    </a>
                  </div>
                )}
                {error && <div className="error-message">{error}</div>}

                {needsPayment ? (
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <StripePaymentForm
                      onPaymentSuccess={handleStripePaymentSuccess}
                      onPaymentError={handleStripePaymentError}
                      isSubmitting={isSubmitting}
                      reservationCost={reservationCost}
                    />
                  </Elements>
                ) : !clientSecret && reservationCost > 0 ? (
                  <div className="loading-feedback">
                    <div className="loading-spinner" />
                    <p>Loading payment form...</p>
                  </div>
                ) : null}
                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="confirm-button"
                    disabled={
                      isSubmitting || (reservationCost > 0 && !clientSecret)
                    }
                  >
                    {isSubmitting
                      ? "Processing..."
                      : reservationCost > 0 && !clientSecret
                        ? "Loading..."
                        : "Reserve Spot"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventRegistrationModal;
